import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Box, 
    Typography, 
    TextField, 
    Button, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Checkbox, 
    FormControlLabel,
    IconButton,
    Alert,
    Grid,
    Divider,
    CircularProgress,
    Autocomplete,
    Chip,
    Paper
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';
import { addSymbol } from '../../api/symbolApi';

// Prevent scroll wheel from changing numeric input values
const preventScrollOnNumberInputs = () => {
    const handleWheel = (e) => {
        if (e.target.type === 'number' && e.target.closest('.MuiTextField-root')) {
            e.preventDefault();
        }
    };

    // Add event listener to prevent scroll on number inputs
    document.addEventListener('wheel', handleWheel, { passive: false });
    
    // Cleanup function
    return () => {
        document.removeEventListener('wheel', handleWheel);
    };
};

// Validation schema
const validationSchema = yup.object({
    name: yup
        .string()
        .required('Symbol name is required')
        .min(1, 'Name must be at least 1 character')
        .max(50, 'Name cannot exceed 50 characters'),
    description: yup
        .string()
        .max(200, 'Description cannot exceed 200 characters')
        .nullable(),
    lpSymbolName: yup
        .string()
        .max(50, 'LP Symbol name cannot exceed 50 characters')
        .nullable(),
    digits: yup
        .number()
        .min(0, 'Digits must be at least 0')
        .max(10, 'Digits cannot exceed 10')
        .nullable(),
    contractSize: yup
        .number()
        .min(0, 'Contract size must be at least 0')
        .nullable(),
    minLotSize: yup
        .number()
        .min(0, 'Minimum lot size must be at least 0')
        .nullable(),
    maxLotSize: yup
        .number()
        .min(0, 'Maximum lot size must be at least 0')
        .nullable(),
    lotStep: yup
        .number()
        .min(0, 'Lot step must be at least 0')
        .nullable(),
    tickSize: yup
        .number()
        .min(0, 'Tick size must be at least 0')
        .nullable(),
    baseCurrency: yup
        .string()
        .max(10, 'Base currency cannot exceed 10 characters')
        .nullable(),
    quoteCurrency: yup
        .string()
        .max(10, 'Quote currency cannot exceed 10 characters')
        .nullable(),
    fixSymbol: yup
        .string()
        .max(50, 'FIX symbol cannot exceed 50 characters')
        .nullable(),
    securityType: yup
        .string()
        .required('Security type is required')
        .max(50, 'Security type cannot exceed 50 characters'),
    isActive: yup.boolean()
});

const AddSymbolModal = ({ open, onClose, onSuccess, lpId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Prevent scroll wheel from changing numeric input values
    useEffect(() => {
        const cleanup = preventScrollOnNumberInputs();
        return cleanup;
    }, []);
    const [baseCurrencyOptions, setBaseCurrencyOptions] = useState([]);
    const [quoteCurrencyOptions, setQuoteCurrencyOptions] = useState([]);

    // Reset form when modal opens/closes
    React.useEffect(() => {
        if (open) {
            setError(null);
            setLoading(false);
        }
    }, [open]);

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
            lpId: lpId || 0,
            lpSymbolName: '',
            digits: 5,
            contractSize: 100000,
            minLotSize: 0.01,
            maxLotSize: 100,
            lotStep: 0.01,
            tickSize: 0.00001,
            baseCurrency: '',
            quoteCurrency: '',
            isActive: true,
            fixSymbol: '',
            securityType: 'Forex'
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setLoading(true);
            setError(null);

            try {
                // Get token from localStorage
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('Authentication token not found');
                }

                // Prepare data for API call
                const symbolData = {
                    name: values.name,
                    description: values.description || null,
                    lpId: values.lpId,
                    lpSymbolName: values.lpSymbolName || null,
                    digits: values.digits || null,
                    contractSize: values.contractSize || null,
                    minLotSize: values.minLotSize || null,
                    maxLotSize: values.maxLotSize || null,
                    lotStep: values.lotStep || null,
                    tickSize: values.tickSize || null,
                    baseCurrency: values.baseCurrency || null,
                    quoteCurrency: values.quoteCurrency || null,
                    isActive: values.isActive,
                    fixSymbol: values.fixSymbol || null,
                    securityType: values.securityType || null
                };

                console.log('Submitting Symbol Data:', symbolData);

                // Call API
                const result = await addSymbol(token, symbolData);
                
                // Success
                toast.success(`Symbol "${values.name}" added successfully!`);
                formik.resetForm();
                onSuccess && onSuccess(result);
                onClose();
                
            } catch (error) {
                console.error('Error adding symbol:', error);
                
                // Handle specific error messages from the API
                let errorMessage = 'Failed to add symbol';
                
                if (error.message) {
                    errorMessage = error.message;
                } else if (error.error) {
                    errorMessage = error.error;
                } else if (typeof error === 'string') {
                    errorMessage = error;
                }
                
                // Check for duplicate symbol error
                if (errorMessage.includes('already exists')) {
                    errorMessage = `❌ ${errorMessage}\n\n💡 Please choose a different symbol name or LP symbol name.`;
                } else if (errorMessage.includes('validation')) {
                    errorMessage = `❌ Validation Error: ${errorMessage}\n\n💡 Please check all required fields and try again.`;
                }
                
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        }
    });

    const handleClose = () => {
        if (!loading) {
            formik.resetForm();
            setError(null);
            onClose();
        }
    };

    const securityTypes = [
        'Forex',
        'Metal',
        'Index',
        'Crypto',
        'Stock',
        'Commodity',
        'Bond',
        'Future',
        'Option'
    ];

    const defaultCurrencies = [
        'USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'SEK', 'NOK', 'DKK',
        'XAU', 'XAG', 'XPT', 'XPD', 'BTC', 'ETH', 'LTC', 'XRP', 'ADA', 'DOT', 'SOL',
        'BCH', 'EOS', 'TRX', 'LINK', 'UNI', 'AAVE', 'COMP', 'MKR', 'SNX', 'YFI'
    ];

    // Initialize currency options when modal opens
    React.useEffect(() => {
        if (open) {
            setBaseCurrencyOptions(defaultCurrencies);
            setQuoteCurrencyOptions(defaultCurrencies);
        }
    }, [open]);

    return (
        <>
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="add-symbol-modal-title"
            aria-describedby="add-symbol-modal-description"
            sx={{
                zIndex: 1300
            }}
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
                maxWidth: 1000,
                maxHeight: '90vh',
                bgcolor: 'rgba(24, 31, 44, 0.95)',
                border: '2px solid rgba(6, 182, 212, 0.3)',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'rgba(6, 182, 212, 0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AddIcon sx={{ color: '#ffd600', fontSize: 28 }} />
                        <Box>
                            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                                Add New Symbol
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                Create a new trading symbol for this liquidity provider
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={handleClose} 
                        disabled={loading}
                        sx={{ color: '#fff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mb: 3,
                                background: 'rgba(211, 47, 47, 0.1)',
                                border: '1px solid #d32f2f',
                                color: '#fff',
                                '& .MuiAlert-icon': { color: '#ff6b6b' },
                                '& .MuiAlert-message': { 
                                    whiteSpace: 'pre-line',
                                    fontSize: '0.9rem'
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={3}>
                            {/* Basic Information */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#1de9b6', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InfoIcon sx={{ fontSize: 20 }} />
                                    Basic Information
                                </Typography>
                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', mb: 2 }} />
                                
                                {/* Information Alert */}
                                <Box sx={{
                                    background: 'rgba(6, 182, 212, 0.1)',
                                    border: '1px solid rgba(6, 182, 212, 0.3)',
                                    borderRadius: 1,
                                    p: 2,
                                    mb: 2
                                }}>
                                    <Typography variant="body2" sx={{ color: '#bfc9db', mb: 1 }}>
                                        <strong>💡 Important:</strong> Symbol names must be unique within this liquidity provider.
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                        • Symbol Name: Must be unique for this LP<br/>
                                        • LP Symbol Name: Must be unique for this LP (if provided)<br/>
                                        • Use descriptive names like "EURUSD", "GBPUSD", etc.
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    id="name"
                                    name="name"
                                    label="Symbol Name *"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    id="lpSymbolName"
                                    name="lpSymbolName"
                                    label="LP Symbol Name"
                                    value={formik.values.lpSymbolName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.lpSymbolName && Boolean(formik.errors.lpSymbolName)}
                                    helperText={formik.touched.lpSymbolName && formik.errors.lpSymbolName}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    id="description"
                                    name="description"
                                    label="Description"
                                    multiline
                                    rows={2}
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.description && Boolean(formik.errors.description)}
                                    helperText={formik.touched.description && formik.errors.description}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            {/* Trading Parameters */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#1de9b6', mb: 2, mt: 2 }}>
                                    Trading Parameters
                                </Typography>
                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="digits"
                                    name="digits"
                                    label="Decimal Digits"
                                    type="number"
                                    value={formik.values.digits}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.digits && Boolean(formik.errors.digits)}
                                    helperText={formik.touched.digits && formik.errors.digits}
                                    inputProps={{ min: 0, max: 10 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="contractSize"
                                    name="contractSize"
                                    label="Contract Size"
                                    type="number"
                                    value={formik.values.contractSize}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.contractSize && Boolean(formik.errors.contractSize)}
                                    helperText={formik.touched.contractSize && formik.errors.contractSize}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="tickSize"
                                    name="tickSize"
                                    label="Tick Size"
                                    type="number"
                                    value={formik.values.tickSize}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.tickSize && Boolean(formik.errors.tickSize)}
                                    helperText={formik.touched.tickSize && formik.errors.tickSize}
                                    inputProps={{ min: 0, step: 0.00001 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            {/* Lot Sizes */}
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="minLotSize"
                                    name="minLotSize"
                                    label="Min Lot Size"
                                    type="number"
                                    value={formik.values.minLotSize}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.minLotSize && Boolean(formik.errors.minLotSize)}
                                    helperText={formik.touched.minLotSize && formik.errors.minLotSize}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="maxLotSize"
                                    name="maxLotSize"
                                    label="Max Lot Size"
                                    type="number"
                                    value={formik.values.maxLotSize}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.maxLotSize && Boolean(formik.errors.maxLotSize)}
                                    helperText={formik.touched.maxLotSize && formik.errors.maxLotSize}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="lotStep"
                                    name="lotStep"
                                    label="Lot Step"
                                    type="number"
                                    value={formik.values.lotStep}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.lotStep && Boolean(formik.errors.lotStep)}
                                    helperText={formik.touched.lotStep && formik.errors.lotStep}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            {/* Currency Information */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#1de9b6', mb: 2, mt: 2 }}>
                                    Currency Information
                                </Typography>
                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    id="baseCurrency"
                                    freeSolo
                                    options={baseCurrencyOptions}
                                    value={formik.values.baseCurrency}
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue('baseCurrency', newValue || '');
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        formik.setFieldValue('baseCurrency', newInputValue);
                                    }}
                                    onBlur={formik.handleBlur}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Base Currency"
                                            error={formik.touched.baseCurrency && Boolean(formik.errors.baseCurrency)}
                                            helperText={formik.touched.baseCurrency && formik.errors.baseCurrency}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: '#fff',
                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                    '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                },
                                                '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props} sx={{ 
                                            padding: '12px 16px',
                                            borderBottom: '1px solid rgba(6, 182, 212, 0.1)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                                cursor: 'pointer'
                                            },
                                            '&:last-child': {
                                                borderBottom: 'none'
                                            }
                                        }}>
                                            <Typography sx={{ 
                                                color: '#fff',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                letterSpacing: '0.5px'
                                            }}>
                                                {option}
                                            </Typography>
                                        </Box>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={option}
                                                label={option}
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'rgba(6, 182, 212, 0.15)',
                                                    color: '#06b6d4',
                                                    border: '1px solid rgba(6, 182, 212, 0.4)',
                                                    fontWeight: 600,
                                                    fontSize: '12px',
                                                    height: '24px',
                                                    '& .MuiChip-deleteIcon': {
                                                        color: '#06b6d4',
                                                        fontSize: '16px'
                                                    }
                                                }}
                                            />
                                        ))
                                    }
                                    filterOptions={(options, { inputValue }) => {
                                        const filtered = options.filter(option =>
                                            option.toLowerCase().includes(inputValue.toLowerCase())
                                        );
                                        // If input doesn't match any option, add it as a new option
                                        if (inputValue && !options.includes(inputValue.toUpperCase())) {
                                            filtered.push(inputValue.toUpperCase());
                                        }
                                        return filtered;
                                    }}
                                    getOptionLabel={(option) => option}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    PaperComponent={({ children, ...other }) => (
                                        <Paper 
                                            {...other} 
                                            sx={{
                                                backgroundColor: 'rgba(24, 31, 44, 0.95)',
                                                border: '1px solid rgba(6, 182, 212, 0.3)',
                                                borderRadius: 2,
                                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                                                backdropFilter: 'blur(10px)',
                                                '& .MuiAutocomplete-listbox': {
                                                    padding: 0,
                                                    maxHeight: '200px'
                                                }
                                            }}
                                        >
                                            {children}
                                        </Paper>
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    id="quoteCurrency"
                                    freeSolo
                                    options={quoteCurrencyOptions}
                                    value={formik.values.quoteCurrency}
                                    onChange={(event, newValue) => {
                                        formik.setFieldValue('quoteCurrency', newValue || '');
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        formik.setFieldValue('quoteCurrency', newInputValue);
                                    }}
                                    onBlur={formik.handleBlur}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Quote Currency"
                                            error={formik.touched.quoteCurrency && Boolean(formik.errors.quoteCurrency)}
                                            helperText={formik.touched.quoteCurrency && formik.errors.quoteCurrency}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: '#fff',
                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                    '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                },
                                                '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props} sx={{ 
                                            padding: '12px 16px',
                                            borderBottom: '1px solid rgba(6, 182, 212, 0.1)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                                cursor: 'pointer'
                                            },
                                            '&:last-child': {
                                                borderBottom: 'none'
                                            }
                                        }}>
                                            <Typography sx={{ 
                                                color: '#fff',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                letterSpacing: '0.5px'
                                            }}>
                                                {option}
                                            </Typography>
                                        </Box>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={option}
                                                label={option}
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'rgba(6, 182, 212, 0.15)',
                                                    color: '#06b6d4',
                                                    border: '1px solid rgba(6, 182, 212, 0.4)',
                                                    fontWeight: 600,
                                                    fontSize: '12px',
                                                    height: '24px',
                                                    '& .MuiChip-deleteIcon': {
                                                        color: '#06b6d4',
                                                        fontSize: '16px'
                                                    }
                                                }}
                                            />
                                        ))
                                    }
                                    filterOptions={(options, { inputValue }) => {
                                        const filtered = options.filter(option =>
                                            option.toLowerCase().includes(inputValue.toLowerCase())
                                        );
                                        // If input doesn't match any option, add it as a new option
                                        if (inputValue && !options.includes(inputValue.toUpperCase())) {
                                            filtered.push(inputValue.toUpperCase());
                                        }
                                        return filtered;
                                    }}
                                    getOptionLabel={(option) => option}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    PaperComponent={({ children, ...other }) => (
                                        <Paper 
                                            {...other} 
                                            sx={{
                                                backgroundColor: 'rgba(24, 31, 44, 0.95)',
                                                border: '1px solid rgba(6, 182, 212, 0.3)',
                                                borderRadius: 2,
                                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                                                backdropFilter: 'blur(10px)',
                                                '& .MuiAutocomplete-listbox': {
                                                    padding: 0,
                                                    maxHeight: '200px'
                                                }
                                            }}
                                        >
                                            {children}
                                        </Paper>
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ color: '#bfc9db' }}>Security Type *</InputLabel>
                                    <Select
                                        id="securityType"
                                        name="securityType"
                                        value={formik.values.securityType}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.securityType && Boolean(formik.errors.securityType)}
                                        sx={{
                                            color: '#fff',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1de9b6' },
                                            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#ff6b6b' },
                                            '& .MuiSvgIcon-root': { color: '#bfc9db' }
                                        }}
                                    >
                                        {securityTypes.map((type) => (
                                            <MenuItem key={type} value={type}>{type}</MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.securityType && formik.errors.securityType && (
                                        <Typography variant="caption" sx={{ color: '#ff6b6b', mt: 0.5, ml: 2 }}>
                                            {formik.errors.securityType}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            {/* Additional Information */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#1de9b6', mb: 2, mt: 2 }}>
                                    Additional Information
                                </Typography>
                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    id="fixSymbol"
                                    name="fixSymbol"
                                    label="FIX Symbol"
                                    value={formik.values.fixSymbol}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.fixSymbol && Boolean(formik.errors.fixSymbol)}
                                    helperText={formik.touched.fixSymbol && formik.errors.fixSymbol}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="isActive"
                                            name="isActive"
                                            checked={formik.values.isActive}
                                            onChange={formik.handleChange}
                                            sx={{
                                                color: '#1de9b6',
                                                '&.Mui-checked': { color: '#1de9b6' }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: '#fff' }}>
                                            Active Status
                                        </Typography>
                                    }
                                />
                            </Grid>
                        </Grid>
                    </form>
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 3,
                    borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'rgba(6, 182, 212, 0.1)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            color: '#bfc9db',
                            borderColor: 'rgba(6, 182, 212, 0.3)',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                background: 'rgba(6, 182, 212, 0.1)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={formik.handleSubmit}
                        disabled={loading || !formik.isValid}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{
                            background: '#ffd600',
                            color: '#000',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#ffcc02'
                            },
                            '&:disabled': {
                                background: 'rgba(255, 214, 0, 0.3)',
                                color: 'rgba(0, 0, 0, 0.3)'
                            }
                        }}
                    >
                        {loading ? 'Adding...' : 'Add Symbol'}
                    </Button>
                </Box>
            </Box>
        </Modal>
        </>
    );
};

export default AddSymbolModal;

